import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, LOCALE_ID, output, signal } from '@angular/core';
import { FilterDateComponent } from '../../../../../shared/ui/shared-filters/filter-date-component/filter-date-component';
import { FilterProductSearch } from '../../../../../shared/ui/shared-filters/filter-product-search/filter-product-search';
import { FilterSortingComponent } from '../../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { CategoryService } from '../../../../../services/category/category.service';
import {
  CategoryFilters,
  CategoryOrderBy,
  CategoryOrderDir,
  DEFAULT_CATEGORY_FILTERS,
} from './category-filters-component.model';

type TitleResult = { id: number; titleAr: string; titleEn: string };

@Component({
  selector: 'app-category-filters-component',
  imports: [FilterProductSearch, FilterDateComponent, FilterSortingComponent],
  templateUrl: './category-filters-component.html',
  styleUrl: './category-filters-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoryFiltersComponent {
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);
  private categoryService = inject(CategoryService);
  private searchTimeout: ReturnType<typeof setTimeout> | undefined;

  applyFilters = output<CategoryFilters>();
  resetFilters = output<void>();

  isArabic = computed(() => this.locale.startsWith('ar'));

  categoryTitle = signal('');
  createdOnFrom = signal<string | null>(null);
  createdOnTo = signal<string | null>(null);
  lastUpdatedOnFrom = signal<string | null>(null);
  lastUpdatedOnTo = signal<string | null>(null);
  orderBy = signal<CategoryOrderBy>(DEFAULT_CATEGORY_FILTERS.orderBy);
  orderDir = signal<CategoryOrderDir>(DEFAULT_CATEGORY_FILTERS.orderDir);

  titleResults = signal<TitleResult[]>([]);
  titleLoading = signal(false);
  titleError = signal('');
  showTitleDropdown = signal(false);

  createdDateInvalid = signal(false);
  lastUpdatedDateInvalid = signal(false);

  sortOptions = [
    { value: 'CATEGORY_TITLE', labelEn: 'Category', labelAr: 'التصنيف' },
    { value: 'CREATION_DATE', labelEn: 'Created Date', labelAr: 'تاريخ الإنشاء' },
  ];

  constructor() {
    this.destroyRef.onDestroy(() => clearTimeout(this.searchTimeout));
  }

  onTitleSearchChange(value: string) {
    clearTimeout(this.searchTimeout);
    this.titleError.set('');

    if (!value) {
      this.titleResults.set([]);
      this.titleLoading.set(false);
      this.showTitleDropdown.set(false);
      return;
    }

    this.showTitleDropdown.set(true);
    this.titleLoading.set(true);
    this.searchTimeout = setTimeout(() => this.searchTitles(value), 400);
  }

  onTitleSelect(item: TitleResult) {
    this.categoryTitle.set(item.titleEn);
    this.showTitleDropdown.set(false);
  }

  onToggleSortOrder() {
    this.orderDir.update((dir) => (dir === 'DESC' ? 'ASC' : 'DESC'));
    this.onApplyFilters();
  }

  onApplyFilters() {
    const createdInvalid = this.isInvalidRange(this.createdOnFrom(), this.createdOnTo());
    const lastUpdatedInvalid = this.isInvalidRange(this.lastUpdatedOnFrom(), this.lastUpdatedOnTo());
    this.createdDateInvalid.set(createdInvalid);
    this.lastUpdatedDateInvalid.set(lastUpdatedInvalid);
    if (createdInvalid || lastUpdatedInvalid) return;

    this.applyFilters.emit({
      categoryTitle: this.categoryTitle(),
      createdOnFrom: this.createdOnFrom(),
      createdOnTo: this.createdOnTo(),
      lastUpdatedOnFrom: this.lastUpdatedOnFrom(),
      lastUpdatedOnTo: this.lastUpdatedOnTo(),
      orderBy: this.orderBy(),
      orderDir: this.orderDir(),
    });
  }

  onResetFilters() {
    clearTimeout(this.searchTimeout);
    this.categoryTitle.set('');
    this.createdOnFrom.set(null);
    this.createdOnTo.set(null);
    this.lastUpdatedOnFrom.set(null);
    this.lastUpdatedOnTo.set(null);
    this.orderBy.set(DEFAULT_CATEGORY_FILTERS.orderBy);
    this.orderDir.set(DEFAULT_CATEGORY_FILTERS.orderDir);
    this.titleResults.set([]);
    this.showTitleDropdown.set(false);
    this.createdDateInvalid.set(false);
    this.lastUpdatedDateInvalid.set(false);
    this.resetFilters.emit();
  }

  onContainerClick(event: Event) {
    event.stopPropagation();
  }

  private isInvalidRange(from: string | null, to: string | null): boolean {
    return !!from && !!to && from > to;
  }

  private searchTitles(term: string) {
    const subscription = this.categoryService.getAllCategories({ categoryTitle: term }).subscribe({
      next: (res) => {
        this.titleResults.set(
          (res.list ?? []).map((c) => ({ id: c.id, titleAr: c.titleAr ?? c.titleEn, titleEn: c.titleEn })),
        );
        this.titleLoading.set(false);
      },
      error: () => {
        this.titleResults.set([]);
        this.titleLoading.set(false);
        this.titleError.set(this.isArabic() ? 'حدث خطأ' : 'Error fetching results');
      },
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }
}
